const STORAGE_KEYS = {
  CURRENT_GAME: 'boosty_chess_current',
  SAVED_GAMES: 'boosty_chess_saved',
  STATS: 'boosty_chess_stats',
  SETTINGS: 'boosty_chess_settings'
};

// Current game
export function saveCurrentGame(gameData) {
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME, JSON.stringify({
      ...gameData,
      savedAt: new Date().toISOString()
    }));
    return true;
  } catch (e) {
    console.error('Failed to save current game:', e);
    return false;
  }
}

export function loadCurrentGame() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CURRENT_GAME);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error('Failed to load current game:', e);
    return null;
  }
}

export function clearCurrentGame() {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME);
}

// Saved games
export function saveGame(name, gameData) {
  try {
    const saved = getSavedGames();
    const newGame = {
      id: crypto.randomUUID(),
      name,
      ...gameData,
      savedAt: new Date().toISOString()
    };
    
    // Limit to 20 games
    if (saved.length >= 20) {
      saved.pop();
    }
    
    saved.unshift(newGame);
    localStorage.setItem(STORAGE_KEYS.SAVED_GAMES, JSON.stringify(saved));
    return newGame;
  } catch (e) {
    console.error('Failed to save game:', e);
    return null;
  }
}

export function getSavedGames() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SAVED_GAMES);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error('Failed to get saved games:', e);
    return [];
  }
}

export function deleteSavedGame(id) {
  try {
    const saved = getSavedGames();
    const filtered = saved.filter(g => g.id !== id);
    localStorage.setItem(STORAGE_KEYS.SAVED_GAMES, JSON.stringify(filtered));
    return true;
  } catch (e) {
    console.error('Failed to delete game:', e);
    return false;
  }
}

export function loadSavedGame(id) {
  const saved = getSavedGames();
  return saved.find(g => g.id === id) || null;
}

// Statistics
export function getStats() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.STATS);
    return data ? JSON.parse(data) : {
      gamesPlayed: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      totalMoves: 0,
      blunders: 0,
      lastPlayed: null
    };
  } catch (e) {
    console.error('Failed to get stats:', e);
    return { gamesPlayed: 0, wins: 0, losses: 0, draws: 0, totalMoves: 0, blunders: 0, lastPlayed: null };
  }
}

export function updateStats(result, moveCount, blunderCount) {
  try {
    const stats = getStats();
    stats.gamesPlayed++;
    stats.totalMoves += moveCount;
    stats.blunders += blunderCount;
    stats.lastPlayed = new Date().toISOString();
    
    if (result === 'win') stats.wins++;
    else if (result === 'loss') stats.losses++;
    else if (result === 'draw') stats.draws++;
    
    localStorage.setItem(STORAGE_KEYS.STATS, JSON.stringify(stats));
    return stats;
  } catch (e) {
    console.error('Failed to update stats:', e);
    return null;
  }
}

export function resetStats() {
  localStorage.removeItem(STORAGE_KEYS.STATS);
}

// Settings
export function getSettings() {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return data ? JSON.parse(data) : {
      boardTheme: 'cosmic',
      soundEnabled: true,
      showLegalMoves: true,
      showCoordinates: true,
      confirmMoves: false,
      aiDifficulty: 'medium',
      coachEnabled: true
    };
  } catch (e) {
    console.error('Failed to get settings:', e);
    return {
      boardTheme: 'cosmic',
      soundEnabled: true,
      showLegalMoves: true,
      showCoordinates: true,
      confirmMoves: false,
      aiDifficulty: 'medium',
      coachEnabled: true
    };
  }
}

export function saveSettings(settings) {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
    return true;
  } catch (e) {
    console.error('Failed to save settings:', e);
    return false;
  }
}

// Export game as PGN
export function exportPGN(moves, result, playerColor, date) {
  const dateStr = date ? new Date(date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
  
  let pgn = `[Event "Boosty Chess Game"]\n`;
  pgn += `[Site "Browser"]\n`;
  pgn += `[Date "${dateStr.replace(/-/g, '.')}"]\n`;
  pgn += `[White "${playerColor === 'white' ? 'Player' : 'AI'}"]\n`;
  pgn += `[Black "${playerColor === 'black' ? 'Player' : 'AI'}"]\n`;
  pgn += `[Result "${result || '*'}"]\n\n`;
  
  // Format moves
  for (let i = 0; i < moves.length; i += 2) {
    const moveNum = Math.floor(i / 2) + 1;
    pgn += `${moveNum}. ${moves[i]}`;
    if (moves[i + 1]) {
      pgn += ` ${moves[i + 1]} `;
    }
  }
  
  if (result) {
    pgn += ` ${result}`;
  }
  
  return pgn;
}

// Import game from PGN
export function importPGN(pgn) {
  try {
    const moves = [];
    
    // Remove headers
    const moveText = pgn.replace(/\[.*?\]/g, '').trim();
    
    // Parse moves (simplified parser)
    const tokens = moveText.split(/\s+/);
    
    for (const token of tokens) {
      // Skip move numbers and results
      if (/^\d+\./.test(token)) continue;
      if (['1-0', '0-1', '1/2-1/2', '*'].includes(token)) continue;
      
      // This is a move
      if (/^[KQRBNP]?[a-h]?[1-8]?x?[a-h][1-8]/.test(token) || 
          token === 'O-O' || token === 'O-O-O') {
        moves.push(token.replace(/[+#!?]/g, ''));
      }
    }
    
    return moves;
  } catch (e) {
    console.error('Failed to import PGN:', e);
    return null;
  }
}
